import {
  ProductCardCompareLabelWeightsType,
  ProductCardMisureDiRischioType,
} from "types/AssistantTypes";
import { ProductTypeEnum } from "types/ProductTypes";

// Sharpe and Information Ratio are pure numbers; every other risk measure is a percentage
const RATIO_MEASURES = new Set(["Sharpe Ratio 3y", "Information Ratio 3y"]);

/**
 * A risk measure value with its unit: «12,46%» for a percentage, «1,45» for a ratio
 * (the table header carries no unit since the rows mix both). «-» / «n.a.» unchanged.
 */
export const withRiskMeasureUnit = <T,>(label: string, value: T): T | string =>
  !RATIO_MEASURES.has(label) && /^-?\d[\d.,]*$/.test(String(value)) ? `${value}%` : value;

const isValueZeroOrEmpty = (value: string | number): boolean => {
  return value === "-" || value === "0" || value === 0;
};

const isProductBond = (productType: string): boolean => {
  return productType === ProductTypeEnum.BOND;
};

/**
 * Checks if all products in the comparison are bonds
 */
export const areAllProductsBonds = (
  productTypes: Record<string, string>,
): boolean => {
  return Object.values(productTypes).every(
    (type) => type === ProductTypeEnum.BOND,
  );
};

/**
 * Checks if any product in the comparison is a bond
 */
const hasAnyBond = (productTypes: Record<string, string>): boolean => {
  return Object.values(productTypes).some(
    (type) => type === ProductTypeEnum.BOND,
  );
};

/**
 * Maps "-" values to "n.a." for bond products in misure di rischio data
 * For specific metrics (Tracking error, Information Ratio), replaces bond values with "-"
 */
export const mapMisureDiRischioData = (
  data: ProductCardMisureDiRischioType[],
  productTypes: Record<string, string>,
  productIsins: string[],
): ProductCardMisureDiRischioType[] => {
  if (!hasAnyBond(productTypes)) return data;

  const bondSpecificMetrics = [
    "Tracking error volatility 3y",
    "Information Ratio 3y",
  ];

  return data.map((item) => {
    const isBondSpecificMetric = bondSpecificMetrics.includes(item.label);

    return {
      ...item,
      product_values: item.product_values.map((value, index) => {
        const productIsin = productIsins[index];
        const isBond = isProductBond(productTypes[productIsin]);

        // For bond-specific metrics, show "-" for bonds (both product and category)
        if (isBond && isBondSpecificMetric) {
          return "-";
        }

        // For other metrics, map zero/empty to "n.a." for bonds
        return isBond && isValueZeroOrEmpty(value) ? "n.a." : value;
      }),
      category_values: item.category_values.map((value, index) => {
        const productIsin = productIsins[index];
        const isBond = isProductBond(productTypes[productIsin]);

        // For bond-specific metrics, always show "-" for bonds (regardless of actual value)
        if (isBond) {
          return "-";
        }

        // For other metrics, map zero/empty to "n.a." for bonds
        return isBond && isValueZeroOrEmpty(value) ? "n.a." : value;
      }),
    };
  });
};

/**
 * Maps category values to "-" for bond products in rendimenti cumulati data
 */
export const mapRendimentiCumulatiData = (
  data: ProductCardMisureDiRischioType[],
  productTypes: Record<string, string>,
  productIsins: string[],
): ProductCardMisureDiRischioType[] => {
  if (!hasAnyBond(productTypes)) return data;

  return data.map((item) => {
    return {
      ...item,
      product_values: item.product_values.map((value, index) => {
        const productIsin = productIsins[index];
        const isBond = isProductBond(productTypes[productIsin]);
        // For product values, map zero/empty to "n.a." for bonds
        return isBond && isValueZeroOrEmpty(value) ? "n.a." : value;
      }),
      category_values: item.category_values.map((value, index) => {
        const productIsin = productIsins[index];
        const isBond = isProductBond(productTypes[productIsin]);
        // For category values, always show "-" for bonds
        if (isBond) {
          return "-";
        }
        return value;
      }),
    };
  });
};

/**
 * Maps "-" values to "n.a." for bond products in settoriale data
 */
export const mapSettorialeData = (
  data: ProductCardCompareLabelWeightsType[],
  productTypes: Record<string, string>,
  productIsins: string[],
): ProductCardCompareLabelWeightsType[] => {
  if (!hasAnyBond(productTypes)) return data;

  return data.map((item) => ({
    ...item,
    weights: item.weights.map((value, index) => {
      const productIsin = productIsins[index];
      const isBond = isProductBond(productTypes[productIsin]);
      return isBond ? "n.a." : value;
    }),
  }));
};
